import React from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox, Icon, Tooltip, Typography } from '@saviynt/design-system';

import { testIds } from '../../../../shared/constants';
import { useInvertedLocalize } from '../../../../shared/hooks';
import { FIXED_AUDIT_COLUMNS } from '../constants';
import type { AuditLogColumnOption } from '../types';

import './AuditLogColumnSortableItem.css';

interface AuditLogColumnSortableItemProps {
  option: AuditLogColumnOption;
  handleChange: (optionValue: string) => void;
  getTooltipText: (option: AuditLogColumnOption) => string | null;
  getTooltipTrigger: (option: AuditLogColumnOption) => React.ReactElement;
}

const TOOLTIP_DELAY_IN_MS = 500;

/**
 * Draggable/Checkable item row for a single column option in the picker.
 */
function AuditLogColumnSortableItem({
  option,
  handleChange,
  getTooltipText,
  getTooltipTrigger,
}: AuditLogColumnSortableItemProps): React.ReactElement {
  const l = useInvertedLocalize();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: option.value });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const isFixedColumn = FIXED_AUDIT_COLUMNS.includes(option.value);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className='AuditLogColumnSortableItem'
      data-testid={`${testIds.AUDIT_LOG_COLUMN_SORTABLE_ITEM}-${option.value}`}>
      <div className='AuditLogColumnSortableItem-content'>
        {option.isChecked && !isFixedColumn ? (
          <button
            type='button'
            className='AuditLogColumnSortableItem-dragger'
            data-testid={`${testIds.AUDIT_LOG_COLUMN_SORTABLE_ITEM_DRAGGER}-${option.value}`}
            aria-disabled={attributes['aria-disabled']}
            aria-pressed={attributes['aria-pressed']}
            aria-roledescription={attributes['aria-roledescription']}
            aria-describedby={attributes['aria-describedby']}
            tabIndex={attributes.tabIndex}
            onPointerDown={
              listeners?.onPointerDown as
                | React.PointerEventHandler<HTMLButtonElement>
                | undefined
            }
            onPointerMove={
              listeners?.onPointerMove as
                | React.PointerEventHandler<HTMLButtonElement>
                | undefined
            }
            onPointerUp={
              listeners?.onPointerUp as
                | React.PointerEventHandler<HTMLButtonElement>
                | undefined
            }
            onKeyDown={
              listeners?.onKeyDown as
                | React.KeyboardEventHandler<HTMLButtonElement>
                | undefined
            }
            onTouchStart={
              listeners?.onTouchStart as
                | React.TouchEventHandler<HTMLButtonElement>
                | undefined
            }>
            <Tooltip
              isPortalEnabled
              text={l('Drag to Reorder')}
              trigger={
                <Icon
                  kind='DragVertical'
                  color='neutral-600'
                  size='smallMedium'
                />
              }
            />
          </button>
        ) : (
          <span className='AuditLogColumnSortableItem-draggerFiller' />
        )}

        <Typography
          lineLimit={3}
          kind='label'
          htmlFor={option.value}
          className='AuditLogColumnSortableItem-checkBox'>
          <Checkbox
            id={`audit-column-checkbox-${option.value}`}
            name={option.value}
            isChecked={option.isChecked}
            isDisabled={isFixedColumn}
            onChange={() => handleChange(option.value)}
            dataTestId={`${testIds.AUDIT_LOG_COLUMN_SORTABLE_ITEM_CHECKBOX}-${option.value}`}
          />
          <Tooltip
            trigger={getTooltipTrigger(option)}
            openDelay={TOOLTIP_DELAY_IN_MS}
            text={getTooltipText(option) ?? undefined}
            maxWidth='20rem'
          />
        </Typography>
      </div>
    </div>
  );
}

export default AuditLogColumnSortableItem;
